"use client"

import type React from "react"

import { useState, useMemo, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Sparkles, X, Upload, Trash2 } from "lucide-react"
import { CRM_API_BASE } from "@/lib/config"
import { httpJson } from "@/lib/http"

// List of public/free email domains to exclude
const PUBLIC_EMAIL_DOMAINS = [
  "gmail.com",
  "google.com",
  "googlemail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "live.com",
  "aol.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "msn.com",
  "yahoo.co.uk",
  "yahoo.ca",
  "yahoo.com.au",
  "yahoo.fr",
  "yahoo.de",
  "hotmail.co.uk",
  "hotmail.ca",
  "hotmail.fr",
  "hotmail.de",
  "outlook.co.uk",
  "outlook.ca",
  "outlook.fr",
  "outlook.de",
  "live.co.uk",
  "live.ca",
  "live.fr",
  "live.de",
  "protonmail.com",
  "proton.me",
  "tutanota.com",
  "mail.com",
  "yandex.com",
  "zoho.com",
  "fastmail.com",
  "gmx.com",
  "gmx.de",
  "web.de",
  "t-online.de",
  "freenet.de",
  "arcor.de",
  "facebook.com",
  "twitter.com",
  "instagram.com",
  "linkedin.com",
  "microsoft.com",
  "apple.com",
  "amazon.com",
  "netflix.com",
]

// Function to extract domain from email and check if it's a business domain
const extractBusinessDomain = (email: string): string | null => {
  if (!email || !email.includes("@")) {
    return null
  }

  const domain = email.split("@")[1]?.toLowerCase()
  if (!domain) {
    return null
  }

  // Check if it's a public email domain
  if (PUBLIC_EMAIL_DOMAINS.includes(domain)) {
    return null
  }

  return domain
}

// Function to truncate filename intelligently (show start + end with extension)
const truncateFilename = (filename: string, maxLength: number = 30): string => {
  if (filename.length <= maxLength) {
    return filename
  }

  // Split filename and extension
  const lastDotIndex = filename.lastIndexOf(".")
  if (lastDotIndex === -1) {
    // No extension, just truncate normally
    return filename.slice(0, maxLength - 3) + "..."
  }

  const nameWithoutExt = filename.slice(0, lastDotIndex)
  const extension = filename.slice(lastDotIndex)

  // If the extension itself is too long, just truncate normally
  if (extension.length >= maxLength - 3) {
    return filename.slice(0, maxLength - 3) + "..."
  }

  // Calculate how much space we have for the name (minus "..." and extension)
  const availableLength = maxLength - 3 - extension.length

  // Split available length: more at the start, less at the end
  const startLength = Math.ceil(availableLength * 0.6)
  const endLength = Math.floor(availableLength * 0.4)

  const start = nameWithoutExt.slice(0, startLength)
  const end = nameWithoutExt.slice(-endLength)

  return `${start}...${end}${extension}`
}

interface ClientFormProps {
  isOpen: boolean
  onClose: () => void
  onSave: (client: {
    legacy_id: number | null
    budget: number | null
    link_count_requirements: number | null
    email: string | null
    company_name: string
    first_name: string | null
    last_name: string | null
    phone: string | null
    domain: string | null
    account_type: string | null
    industries: number[]
    addresses: number[]
    locations: number[]
    metrics_requirements: number[]
    users: number[]
    company_logo: File | null
    billings: number[]
    primary_user_password: string
    primary_user_password_confirm: string
    _addressData?: {
      line_1: string
      line_2: string
      city: string
      state: string
      zip: string
      reference: string
    } | null
    _billingData?: {
      type: number
      notes: string
    } | null
  }) => Promise<void>
}

export function ClientForm({ isOpen, onClose, onSave }: ClientFormProps) {
  const [client, setClient] = useState({
    email: "",
    company_name: "",
    first_name: "",
    last_name: "",
    phone: "",
    domain: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    company_logo: null as File | null,
    account_type: "",
    industry: "",
    billing_type: "",
    billing_details: "",
    primary_user_password: "",
    primary_user_password_confirm: "",
  })

  // Billing type mapping
  const billingTypeMap: Record<string, number> = {
    "bill.com": 1,
    "Check": 2,
    "PayPal": 3,
    "QuickBooks": 4,
    "Stripe": 5,
    "Wire Transfer": 6,
    "Other": 7,
  }

  const [domainSource, setDomainSource] = useState<"user" | "autofill" | null>(null)
  const [error, setError] = useState("")
  const [errorFields, setErrorFields] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // Industry options state
  const [industryOptions, setIndustryOptions] = useState<Array<{ id: number; text: string }>>([])
  const [loadingIndustries, setLoadingIndustries] = useState(true)
  
  // Fetch industries when modal opens
  useEffect(() => {
    const fetchIndustries = async () => {
      try {
        setLoadingIndustries(true)
        const allIndustries: Array<{ id: number; text: string }> = []
        let offset = 0
        const limit = 100
        let hasMore = true
        
        // Fetch all industries with pagination
        while (hasMore) {
          const url = `${CRM_API_BASE}/industry/?limit=${limit}&offset=${offset}`
          const response = await httpJson<any>(url)
          
          console.log("[ClientForm] Industry API response:", response)
          
          // Handle different response structures
          let rawIndustries: Array<{ id: number; text: string }> = []
          if (Array.isArray(response)) {
            // If response is directly an array
            rawIndustries = response
          } else if (response.results && Array.isArray(response.results)) {
            // If response has results array
            rawIndustries = response.results
          } else if (response.data && Array.isArray(response.data)) {
            // If response has data array
            rawIndustries = response.data
          }
          
          // Use text field directly from API
          const industries: Array<{ id: number; text: string }> = rawIndustries.map((ind) => ({
            id: ind.id,
            text: ind.text || `Industry ${ind.id}`,
          }))
          
          console.log("[ClientForm] Parsed industries:", industries)
          
          allIndustries.push(...industries)
          
          // Check if there are more results
          const totalCount = response.count || response.total || industries.length
          if (industries.length < limit || allIndustries.length >= totalCount) {
            hasMore = false
          } else {
            offset += limit
          }
        }
        
        console.log("[ClientForm] All industries loaded:", allIndustries)
        setIndustryOptions(allIndustries)
      } catch (err) {
        console.error("Error fetching industries:", err)
        setIndustryOptions([])
      } finally {
        setLoadingIndustries(false)
      }
    }
    
    if (isOpen) {
      fetchIndustries()
    }
  }, [isOpen])

  // Validation logic for required fields
  const formValidation = useMemo(() => {
    const requiredFields = {
      company_name: client.company_name?.trim(),
      email: client.email?.trim(),
      first_name: client.first_name?.trim(),
      last_name: client.last_name?.trim(),
      domain: client.domain?.trim(),
      account_type: client.account_type?.trim(),
    }

    // If any address field is filled, then line_1, city, state, and zip are required (line_2 is optional)
    const hasAnyAddressField = client.address_line1?.trim() || 
                              client.address_line2?.trim() || 
                              client.city?.trim() || 
                              client.state?.trim() || 
                              client.zip?.trim()
    
    if (hasAnyAddressField) {
      if (!client.address_line1?.trim()) requiredFields.address_line1 = ""
      if (!client.city?.trim()) requiredFields.city = ""
      if (!client.state?.trim()) requiredFields.state = ""
      if (!client.zip?.trim()) requiredFields.zip = ""
    }

    // If billing_type is "Other", billing_details is required
    if (client.billing_type === "Other" && !client.billing_details?.trim()) {
      requiredFields.billing_details = ""
    }

    const requiredMissing = Object.entries(requiredFields)
      .filter(([_, value]) => !value)
      .map(([field]) => field)

    const pw = client.primary_user_password ?? ""
    const pw2 = client.primary_user_password_confirm ?? ""
    const passwordFieldErrors: string[] = []
    let passwordError = ""

    if (!pw.trim()) {
      passwordFieldErrors.push("primary_user_password")
      passwordError = "Password is required."
    } else if (pw.length < 6) {
      passwordFieldErrors.push("primary_user_password")
      passwordError = "Password must be at least 6 characters."
    }

    if (!pw2.trim()) {
      passwordFieldErrors.push("primary_user_password_confirm")
      if (!passwordError) passwordError = "Please confirm the password."
    } else if (pw.trim() && pw.length >= 6 && pw !== pw2) {
      passwordFieldErrors.push("primary_user_password_confirm")
      passwordError = "Passwords do not match."
    }

    const missingFields = [...requiredMissing, ...passwordFieldErrors]
    const isValid = requiredMissing.length === 0 && passwordFieldErrors.length === 0

    return { isValid, missingFields, requiredMissing, passwordError }
  }, [client])

  // Check if any address field has value to show required asterisks
  const hasAnyAddressField = client.address_line1?.trim() || 
                            client.address_line2?.trim() || 
                            client.city?.trim() || 
                            client.state?.trim() || 
                            client.zip?.trim()

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setClient({
        email: "",
        company_name: "",
        first_name: "",
        last_name: "",
        phone: "",
        domain: "",
        address_line1: "",
        address_line2: "",
        city: "",
        state: "",
        zip: "",
        company_logo: null,
        account_type: "",
        industry: "",
        billing_type: "",
        billing_details: "",
        primary_user_password: "",
        primary_user_password_confirm: "",
      })
      setLogoPreview(null)
      setDomainSource(null)
      setError("")
      setErrorFields([])
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }, [isOpen])

  const handleEmailBlur = () => {
    // Auto-fill domain field when email loses focus
    if (client.email) {
      const businessDomain = extractBusinessDomain(client.email)

      // If domain is empty OR if domain was auto-filled (not user input), update it
      if (!client.domain || domainSource === "autofill") {
        if (businessDomain) {
          setClient((prev) => ({
            ...prev,
            domain: businessDomain,
          }))
          setDomainSource("autofill")
          console.log("[v0] Auto-filled domain on email blur:", businessDomain)
        } else {
          setClient((prev) => ({
            ...prev,
            domain: "",
          }))
          setDomainSource(null)
          console.log("[v0] Email from public domain, cleared domain field on blur")
        }
      } else if (domainSource === "user") {
        console.log("[v0] Domain was manually entered by user, not replacing with:", businessDomain)
      }
    }
  }

  const handleChange = (field: string, value: string | number | null | File) => {
    setClient({ ...client, [field]: value })

    // Clear error messages when user starts typing
    if (error) {
      setError("")
    }
    if (errorFields.length > 0) {
      setErrorFields([])
    }

    // Track if user manually enters domain
    if (field === "domain") {
      setDomainSource("user")
      console.log("[v0] User manually entered domain:", value)
    }
  }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null
    
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file")
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("Image size must be less than 5MB")
        return
      }

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setLogoPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
      
      handleChange("company_logo", file)
    } else {
      handleChange("company_logo", null)
      setLogoPreview(null)
    }
  }

  const handleRemoveLogo = () => {
    handleChange("company_logo", null)
    setLogoPreview(null)
    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (isSubmitting) return // Prevent multiple submissions

    // Validate required fields first
    if (!formValidation.isValid) {
      setError(
        formValidation.requiredMissing.length > 0
          ? "Please fill in all required fields"
          : formValidation.passwordError,
      )
      setErrorFields(formValidation.missingFields)
      setTimeout(() => {
        const focusOrder = [
          "domain",
          "company_name",
          "account_type",
          "address_line1",
          "city",
          "state",
          "zip",
          "billing_details",
          "first_name",
          "last_name",
          "email",
          "primary_user_password",
          "primary_user_password_confirm",
        ]
        for (const fieldId of focusOrder) {
          if (!formValidation.missingFields.includes(fieldId)) continue
          const el = document.getElementById(fieldId)
          if (el) {
            el.focus()
            el.scrollIntoView({ behavior: "smooth", block: "center" })
            break
          }
        }
      }, 100)
      return
    }


    setIsSubmitting(true)
    setError("") // Clear any previous errors
    setErrorFields([])

    try {
      // Prepare address data if all required fields are provided
      // Required: line_1, city, state, zip. Optional: line_2
      const hasAnyAddressField = client.address_line1?.trim() || 
                                 client.address_line2?.trim() || 
                                 client.city?.trim() || 
                                 client.state?.trim() || 
                                 client.zip?.trim()
      
      const hasAllRequiredAddressFields = client.address_line1?.trim() && 
                                         client.city?.trim() && 
                                         client.state?.trim() && 
                                         client.zip?.trim()
      
      // Only create address if all required fields are present
      const addressData = (hasAnyAddressField && hasAllRequiredAddressFields) ? {
        line_1: client.address_line1.trim(),
        ...(client.address_line2?.trim() && { line_2: client.address_line2.trim() }),
        city: client.city.trim(),
        state: client.state.trim(),
        zip_code: client.zip.trim(),
      } : null

      // Step 1: Prepare client data WITHOUT addresses (will be added later)
      const submitData = {
        legacy_id: null,
        budget: null,
        link_count_requirements: null,
        email: client.email || null,
        company_name: client.company_name,
        first_name: client.first_name || null,
        last_name: client.last_name || null,
        phone: client.phone || null,
        domain: client.domain || null,
        account_type: client.account_type ? Number.parseInt(client.account_type) : null,
        industries: client.industry ? [Number.parseInt(client.industry)] : [],
        // If billing_type is not "Other", include billings array with the type ID
        billings: client.billing_type && client.billing_type !== "Other" 
          ? [billingTypeMap[client.billing_type]] 
          : [],
        // DO NOT include addresses here - will be added in step 3
        locations: [], // Empty array since locations are removed
        metrics_requirements: [], // Empty array since metrics are removed
        users: [], // Empty array as per API schema
        company_logo: client.company_logo,
        primary_user_password: client.primary_user_password,
        primary_user_password_confirm: client.primary_user_password_confirm,
        // Pass billing data separately if "Other" is selected
        _billingData: client.billing_type === "Other" ? {
          type: 7,
          notes: client.billing_details || "",
        } : null,
        // Pass address data separately so it can be created after client
        _addressData: addressData,
      }

      const { primary_user_password: _p, primary_user_password_confirm: _pc, ...logSafe } = submitData
      console.log("[v0] Creating client with data (without address):", logSafe)
      await onSave(submitData)
      
      // Clear logo preview after successful submission
      setLogoPreview(null)
    } catch (error: any) {
      // Only log unexpected errors, not validation errors (like signup form does)
      if (error instanceof Error && !error.message.includes("HTTP 400")) {
        console.error("[v0] Error creating client:", error)
      }

      // Handle specific error cases
      if (error.message) {
        const errorMessage = error.message.toLowerCase()
        const errorFields: string[] = []
        let errorText = ""

        // Check for email duplicate error
        if (errorMessage.includes("client with this email already exists")) {
          errorFields.push("email")
          errorText += "This email already exists in Zipsprout. Please use another email or edit the existing client with this email. "
        }

        // Check for domain duplicate error
        if (errorMessage.includes("client with this domain already exist")) {
          errorFields.push("domain")
          errorText += "This domain already exists in Zipsprout. Please use another domain or edit the existing client with this domain."
        }

        // If we found specific errors, show them
        if (errorFields.length > 0) {
          setErrorFields(errorFields)
          setError(errorText.trim())
          
          // Focus and scroll to first error field in UI order (domain comes first)
          setTimeout(() => {
            if (errorFields.includes("domain")) {
              const domainField = document.getElementById("domain")
              if (domainField) {
                domainField.focus()
                domainField.scrollIntoView({ behavior: "smooth", block: "center" })
              }
            } else if (errorFields.includes("email")) {
              const emailField = document.getElementById("email")
              if (emailField) {
                emailField.focus()
                emailField.scrollIntoView({ behavior: "smooth", block: "center" })
              }
            }
          }, 100)
          return
        }
      }

      // Show a user-friendly error message instead of the raw backend error
      setError("Failed to create client. Please check your information and try again.")
      // Scroll to top to show error message
      setTimeout(() => {
        const modalContent = document.querySelector(".bg-background.rounded-lg.shadow-lg")
        if (modalContent) {
          modalContent.scrollTop = 0
        }
      }, 100)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Add New Client</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Invisible dummy button to capture initial focus */}
          <button
            type="button"
            style={{
              position: "absolute",
              width: "1px",
              height: "1px",
              padding: 0,
              margin: "-1px",
              overflow: "hidden",
              clip: "rect(0, 0, 0, 0)",
              whiteSpace: "nowrap",
              border: 0,
            }}
            autoFocus
            tabIndex={0}
            aria-hidden="true"
          />

          <div className="grid gap-6">
            <div className="border border-border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Company Information</h3>

              <div className="space-y-4">
                <div className="grid gap-2">
                  <Label htmlFor="domain">
                    Domain <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="domain"
                      value={client.domain}
                      onChange={(e) => handleChange("domain", e.target.value)}
                      maxLength={255}
                      placeholder="example.com"
                      className={errorFields.includes("domain") ? "border-red-500" : ""}
                    />
                    <Button
                      type="button"
                      variant="default"
                      size="default"
                      onClick={() => {
                        // Placeholder for auto-fill logic to be implemented later
                        console.log("[v0] Auto Fill button clicked - logic to be implemented")
                      }}
                      className="shrink-0"
                      disabled={true}
                      title="Auto-fill feature will be implemented later"
                    >
                      <Sparkles className="mr-2 h-4 w-4" />
                      Auto-fill
                    </Button>
                  </div>
                  {domainSource === "autofill" && (
                    <p className="text-xs text-muted-foreground">Domain auto-filled from email address</p>
                  )}
                  {errorFields.includes("domain") && (
                    <p className="text-xs text-red-500">
                      This domain already exists in Zipsprout. Please use another domain or edit the existing client
                      with this domain.
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="company_name">
                      Company Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="company_name"
                      value={client.company_name}
                      onChange={(e) => handleChange("company_name", e.target.value)}
                      maxLength={120}
                      required
                      placeholder="Enter company name"
                      className={errorFields.includes("company_name") ? "border-red-500" : ""}
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="company_logo">Company Logo</Label>
                    <div className="flex gap-2 items-center min-w-0 overflow-hidden">
                      <Input
                        ref={fileInputRef}
                        id="company_logo"
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                      />
                      {!client.company_logo ? (
                        <Button
                          type="button"
                          variant="default"
                          size="default"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex items-center gap-2 shrink-0"
                        >
                          <Upload className="h-4 w-4" />
                          Choose File
                        </Button>
                      ) : (
                        <>
                          {logoPreview && (
                            <img
                              src={logoPreview}
                              alt="Logo preview"
                              className="h-8 w-8 object-contain rounded border border-border shrink-0"
                            />
                          )}
                          <span className="text-sm text-muted-foreground flex-1 min-w-0 overflow-hidden" title={client.company_logo.name}>
                            {truncateFilename(client.company_logo.name)}
                          </span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="default"
                            onClick={handleRemoveLogo}
                            className="shrink-0 text-destructive hover:text-red-700 hover:bg-transparent"
                          >
                            <Trash2 className="h-4 w-4 transition-transform hover:scale-110" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Help text for Company Logo - positioned below the grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div></div> {/* Empty div to align with Company Logo column */}
                  <div className="-mt-2">
                    <p className="text-xs text-gray-500">Upload company logo (PNG, JPG, or SVG, max 5MB)</p>
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="account_type">
                    Account Type <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={client.account_type}
                    onValueChange={(value) => handleChange("account_type", value)}
                  >
                    <SelectTrigger className={`border-2 ${errorFields.includes("account_type") ? "border-red-500" : "border-gray-300"}`}>
                      <SelectValue placeholder="Select account type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Local Business</SelectItem>
                      <SelectItem value="2">Agency</SelectItem>
                      <SelectItem value="3">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                  {errorFields.includes("account_type") && (
                    <p className="text-xs text-red-500">Account Type is required</p>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="industry">Industry</Label>
                  <Select
                    value={client.industry}
                    onValueChange={(value) => handleChange("industry", value)}
                    disabled
                  >
                    <SelectTrigger className="border-2 border-gray-300">
                      <SelectValue placeholder="Coming soon" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingIndustries ? (
                        <SelectItem value="loading" disabled>Loading industries...</SelectItem>
                      ) : industryOptions.length > 0 ? (
                        industryOptions.map((industry) => (
                          <SelectItem key={industry.id} value={industry.id.toString()}>
                            {industry.text}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-industries" disabled>No industries available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="billing_type">Billing Type</Label>
                  <Select
                    value={client.billing_type}
                    onValueChange={(value) => {
                      // Update both fields in a single state update
                      setClient({
                        ...client,
                        billing_type: value,
                        billing_details: value !== "Other" ? "" : client.billing_details,
                      })
                      // Clear error messages when user changes selection
                      if (error) {
                        setError("")
                      }
                      if (errorFields.length > 0) {
                        setErrorFields([])
                      }
                    }}
                  >
                    <SelectTrigger className="border-2 border-gray-300">
                      <SelectValue placeholder="Select billing type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Stripe">Stripe</SelectItem>
                      <SelectItem value="QuickBooks">QuickBooks</SelectItem>
                      <SelectItem value="bill.com">bill.com</SelectItem>
                      <SelectItem value="PayPal">PayPal</SelectItem>
                      <SelectItem value="Check">Check</SelectItem>
                      <SelectItem value="Wire Transfer">Wire Transfer</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {client.billing_type === "Other" && (
                  <div className="grid gap-2">
                    <Label htmlFor="billing_details">
                      Billing Details / Instructions <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="billing_details"
                      value={client.billing_details}
                      onChange={(e) => handleChange("billing_details", e.target.value)}
                      placeholder="Enter billing details or instructions"
                      className={errorFields.includes("billing_details") ? "border-red-500" : "border-2 border-gray-300"}
                      rows={3}
                    />
                    {errorFields.includes("billing_details") && (
                      <p className="text-xs text-red-500">Billing Details / Instructions is required when Billing Type is Other</p>
                    )}
                  </div>
                )}

                <div className="grid gap-2">
                  <Label htmlFor="address_line1">
                    Address Line 1<span className="text-red-500"> *</span>
                  </Label>
                  <Input
                    id="address_line1"
                    value={client.address_line1}
                    onChange={(e) => handleChange("address_line1", e.target.value)}
                    maxLength={255}
                    placeholder="Street address"
                    className={errorFields.includes("address_line1") ? "border-red-500" : ""}
                  />
                  {errorFields.includes("address_line1") && (
                    <p className="text-xs text-red-500">Address Line 1 is required when providing address information</p>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="address_line2">Address Line 2 (optional)</Label>
                  <Input
                    id="address_line2"
                    value={client.address_line2}
                    onChange={(e) => handleChange("address_line2", e.target.value)}
                    maxLength={255}
                    placeholder="Apartment, suite, etc. (optional)"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="city">
                      City<span className="text-red-500"> *</span>
                    </Label>
                    <Input
                      id="city"
                      value={client.city}
                      onChange={(e) => handleChange("city", e.target.value)}
                      maxLength={100}
                      placeholder="City"
                      className={errorFields.includes("city") ? "border-red-500" : ""}
                    />
                    {errorFields.includes("city") && (
                      <p className="text-xs text-red-500">City is required when providing address information</p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="state">
                      State<span className="text-red-500"> *</span>
                    </Label>
                    <Input
                      id="state"
                      value={client.state}
                      onChange={(e) => handleChange("state", e.target.value)}
                      maxLength={50}
                      placeholder="State"
                      className={errorFields.includes("state") ? "border-red-500" : ""}
                    />
                    {errorFields.includes("state") && (
                      <p className="text-xs text-red-500">State is required when providing address information</p>
                    )}
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="zip">
                      ZIP Code<span className="text-red-500"> *</span>
                    </Label>
                    <Input
                      id="zip"
                      value={client.zip}
                      onChange={(e) => handleChange("zip", e.target.value)}
                      maxLength={20}
                      placeholder="ZIP"
                      className={errorFields.includes("zip") ? "border-red-500" : ""}
                    />
                    {errorFields.includes("zip") && (
                      <p className="text-xs text-red-500">ZIP Code is required when providing address information</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="border border-border rounded-lg p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">Primary Contact</h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="first_name">
                      First Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="first_name"
                      value={client.first_name}
                      onChange={(e) => handleChange("first_name", e.target.value)}
                      maxLength={255}
                      placeholder="First name"
                      className={errorFields.includes("first_name") ? "border-red-500" : ""}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="last_name">
                      Last Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="last_name"
                      value={client.last_name}
                      onChange={(e) => handleChange("last_name", e.target.value)}
                      maxLength={255}
                      placeholder="Last name"
                      className={errorFields.includes("last_name") ? "border-red-500" : ""}
                    />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="email">
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={client.email}
                    onChange={(e) => handleChange("email", e.target.value)}
                    onBlur={handleEmailBlur}
                    maxLength={255}
                    placeholder="email@example.com"
                    className={errorFields.includes("email") ? "border-red-500" : ""}
                  />
                  {errorFields.includes("email") && (
                    <p className="text-xs text-red-500">
                      This email already exists in Zipsprout. Please use another email or edit the existing client with this email.
                    </p>
                  )}
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={client.phone}
                    onChange={(e) => handleChange("phone", e.target.value)}
                    maxLength={255}
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 items-start">
                  <div className="grid gap-2">
                    <Label htmlFor="primary_user_password">
                      Password <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="primary_user_password"
                      type="password"
                      autoComplete="new-password"
                      value={client.primary_user_password}
                      onChange={(e) => handleChange("primary_user_password", e.target.value)}
                      maxLength={128}
                      placeholder="Min. 6 characters"
                      className={errorFields.includes("primary_user_password") ? "border-red-500" : ""}
                    />
                    <div className="min-h-[2.75rem] text-xs leading-snug">
                      {errorFields.includes("primary_user_password") ? (
                        <p className="text-red-500 m-0">
                          {!client.primary_user_password?.trim()
                            ? "Password is required."
                            : "Password must be at least 6 characters."}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="primary_user_password_confirm">
                      Confirm password <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="primary_user_password_confirm"
                      type="password"
                      autoComplete="new-password"
                      value={client.primary_user_password_confirm}
                      onChange={(e) => handleChange("primary_user_password_confirm", e.target.value)}
                      maxLength={128}
                      placeholder="Re-enter password"
                      className={errorFields.includes("primary_user_password_confirm") ? "border-red-500" : ""}
                    />
                    <div className="min-h-[2.75rem] text-xs leading-snug">
                      {errorFields.includes("primary_user_password_confirm") ? (
                        <p className="text-red-500 m-0">
                          {!client.primary_user_password_confirm?.trim()
                            ? "Please confirm the password."
                            : "Passwords do not match."}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2 mt-6 pt-6 border-t">
            <Button variant="outline" type="button" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Creating Client...
                </>
              ) : (
                "Save Client"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
